import { deliveryValidationLabel, type DeliveryLabelLanguage } from './deliveryValidationLabel';

type AnyRecord = Record<string, any>;

export interface ValidationRootCauseGroup {
  key: string;
  title: string;
  category?: string;
  severity?: string;
  count: number;
  lines: number[];
  messages: string[];
  deterministicRepairId?: string;
  resolved?: boolean;
}

export interface ValidationRootCauseSummary {
  rootCauseCount: number;
  errorRootCauseCount: number;
  warningRootCauseCount: number;
  rawIssueCount: number;
  /**
   * What the checker reported, before identical findings were folded together.
   *
   * Without it the turn showed two numbers for one set of findings and no way to
   * reconcile them: the step said "6 个错误、11 个警告" (17) and the section under
   * it said "6 类根因（共 15 处）". Neither was wrong — 17 is what came back, 15
   * is what is left once exact duplicates are removed — but the reader had to
   * take both on faith. Carrying the before-count lets the header say so.
   */
  reportedIssueCount: number;
  groups: ValidationRootCauseGroup[];
}

function asRecord(value: unknown): AnyRecord | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as AnyRecord : null;
}

function asArray(value: unknown): AnyRecord[] {
  return Array.isArray(value)
    ? value.map(asRecord).filter((item): item is AnyRecord => Boolean(item))
    : [];
}

function hasText(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function normalizeText(value: unknown): string {
  return hasText(value) ? value.trim() : '';
}

function normalizeSeverity(value: unknown): string {
  const severity = normalizeText(value).toLowerCase();
  if (severity === 'error' || severity === 'fatal') return 'error';
  if (severity === 'warning' || severity === 'warn') return 'warning';
  if (severity === 'info') return 'info';
  return severity || 'warning';
}

function issueLine(issue: AnyRecord): number | undefined {
  const candidates = [issue.line, issue.lineNumber, issue.startLine, issue.location?.line, issue.range?.start?.line];
  for (const candidate of candidates) {
    const n = Number(candidate);
    if (Number.isFinite(n) && n > 0) return Math.floor(n);
  }
  return undefined;
}

/** What makes two entries the same finding: its id, else its code, line and wording. Empty when it has none of them. */
function findingKey(issue: AnyRecord): string {
  return normalizeText(issue.id) || [issue.code, issueLine(issue), issue.message].filter(Boolean).join('|');
}

function titleFromTerminator(term: string): string {
  const normalized = term.toUpperCase();
  if (normalized === 'END_IF') return '控制块结束符缺少分号';
  return `${normalized} 结束符缺少分号`;
}

export function classifyValidationRootCause(issue: unknown): {
  key: string;
  title: string;
  deterministicRepairId?: string;
} {
  const row = asRecord(issue) || {};
  const message = normalizeText(row.message || row.detail || row.reason || row.title);
  const code = normalizeText(row.code || row.ruleId || row.type);
  const haystack = `${code}\n${message}`;

  const terminator = haystack.match(/\b(END_IF|END_FOR|END_CASE|END_WHILE|END_REPEAT)\b/i)?.[1];
  if (
    terminator
    && (
      /missing\s+(?:a\s+)?trailing\s+semicolon/i.test(haystack)
      || /缺少.*分号/.test(haystack)
      || /分号.*缺失/.test(haystack)
    )
  ) {
    return {
      key: 'control_block_terminator_missing_semicolon',
      title: titleFromTerminator(terminator),
      deterministicRepairId: 'fix_control_block_terminator_semicolons',
    };
  }

  if (/missing\s+(?:a\s+)?trailing\s+semicolon/i.test(haystack) || /缺少.*分号/.test(haystack)) {
    return {
      key: 'statement_missing_trailing_semicolon',
      title: '语句缺少结尾分号',
      deterministicRepairId: 'fix_missing_statement_semicolons',
    };
  }

  if (/block\s+structure|control\s+block|unmatched|mismatch|不匹配|块结构|闭合/i.test(haystack)) {
    return {
      key: 'control_block_structure_mismatch',
      title: '控制块结构不匹配',
    };
  }

  const normalizedMessage = message
    .replace(/`[^`]+`/g, '`…`')
    .replace(/\bline\s+\d+\b/gi, 'line …')
    .replace(/第\s*\d+\s*行/g, '第 … 行')
    .replace(/\d+/g, '#')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

  const normalizedCode = code
    .replace(/\d+/g, '#')
    .replace(/\s+/g, '_')
    .toLowerCase();

  const category = normalizeText(row.category || row.kind || row.source).toLowerCase() || 'validation';
  const key = [category, normalizedCode || normalizedMessage || 'issue'].join(':');

  return {
    key,
    title: message || code || '校验问题',
  };
}

function normalizeExistingGroup(group: AnyRecord): ValidationRootCauseGroup | null {
  const key = normalizeText(group.key || group.rootCauseKey || group.patternKey || group.id || group.code);
  const title = normalizeText(group.title || group.label || group.message || group.rootCause || group.description);
  if (!key && !title) return null;

  const lines = Array.isArray(group.lines)
    ? group.lines.map(Number).filter((n: number) => Number.isFinite(n) && n > 0)
    : [];

  const count = Number(group.count ?? group.affectedCount ?? group.rawIssueCount ?? group.occurrences ?? lines.length ?? 1);

  return {
    key: key || title.toLowerCase().replace(/\s+/g, '_'),
    title: title || key,
    category: normalizeText(group.category || group.kind),
    severity: normalizeSeverity(group.severity),
    count: Number.isFinite(count) && count > 0 ? Math.floor(count) : 1,
    lines,
    messages: Array.isArray(group.messages) ? group.messages.map(String).filter(Boolean) : [],
    deterministicRepairId: normalizeText(group.deterministicRepairId || group.repairId),
    resolved: group.resolved === true,
  };
}

export function buildValidationRootCauseSummary(report: unknown): ValidationRootCauseSummary {
  const record = asRecord(report);
  if (!record) {
    return { rootCauseCount: 0, errorRootCauseCount: 0, warningRootCauseCount: 0, rawIssueCount: 0, reportedIssueCount: 0, groups: [] };
  }

  // The groups are made here, from the findings, whenever the report has
  // findings to make them from. One kept on the report was made somewhere else:
  // the gateway attaches its own grouping to the reports it streams, and the
  // stream state attached this module's — so the page read the gateway's
  // grouping while a turn streamed and this one after a reload, two counts of
  // one report. A grouping kept on the report is read only when there is
  // nothing else to read.
  const hasFindings = ['issues', 'errors', 'warnings'].some(field => Array.isArray(record[field]));
  const explicitGroups = hasFindings ? [] : [
    ...asArray(record.rootCauseSummary?.groups),
    ...asArray(record.rootCauseGroups),
    ...asArray(record.issueGroups),
    ...asArray(record.systemicPatterns),
  ].map(normalizeExistingGroup).filter((item): item is ValidationRootCauseGroup => Boolean(item));

  if (explicitGroups.length > 0) {
    const rawIssueCount = Number(record.rootCauseSummary?.rawIssueCount ?? record.rawIssueCount)
      || explicitGroups.reduce((sum, group) => sum + group.count, 0);
    const errorRootCauseCount = explicitGroups.filter(group => normalizeSeverity(group.severity) === 'error').length
      || (record.valid === false ? explicitGroups.length : 0);
    return {
      rootCauseCount: explicitGroups.length,
      errorRootCauseCount,
      warningRootCauseCount: Math.max(0, explicitGroups.length - errorRootCauseCount),
      rawIssueCount,
      reportedIssueCount: Number(record.rootCauseSummary?.reportedIssueCount) || rawIssueCount,
      groups: explicitGroups,
    };
  }

  // Warnings belong here too. Leaving them out meant a report of "1 error, 11
  // warnings" expanded to a single root cause: the other eleven findings had
  // nowhere in the UI to appear at all, and the count in the step heading was
  // the only trace of them.
  //
  // Each list carries its own severity default rather than sharing one, because
  // the fallback below reads `record.valid` — on a failing report that would
  // stamp every warning as an error and turn "1 error" into "12".
  const lists = [
    { items: asArray(record.issues), fallbackSeverity: record.valid === false ? 'error' : 'warning' },
    { items: asArray(record.errors), fallbackSeverity: 'error' },
    { items: asArray(record.warnings), fallbackSeverity: 'warning' },
  ];
  // Every finding once, and only those the step states.
  //
  // A validation report lists every finding under `issues` and lists its errors
  // and warnings a second time under their own names; the validation stage's
  // artifact has only those two lists. Read as three lists, run 2 of 2026-09-28
  // had 42 findings, and the 22 left after folding were stated as 「共 22 处，
  // 另 20 处重复」: twenty duplicates the checker never reported. A finding named
  // again in a later list is the same finding; one repeated within a list is a
  // duplicate the checker made, and is folded below and stated.
  //
  // And of those, the errors and warnings: the step above says 「1 个错误、19 个
  // 警告」, and the notes a report carries beside them (`info`: a test hint, a
  // structure suggestion, a simulation that did not run) are neither. Counted
  // in, the same report read 22 under a row that says 20.
  const namedEarlier = new Set<string>();
  const issues: Array<{ issue: AnyRecord; severity: string }> = [];
  for (const { items, fallbackSeverity } of lists) {
    const named: string[] = [];
    for (const issue of items) {
      const key = findingKey(issue);
      if (key && namedEarlier.has(key)) continue;
      if (key) named.push(key);
      const severity = normalizeSeverity(issue.severity || issue.level || fallbackSeverity);
      if (severity === 'error' || severity === 'warning') issues.push({ issue, severity });
    }
    named.forEach(key => namedEarlier.add(key));
  }
  const seenIssueIds = new Set<string>();
  const uniqueIssues = issues.filter(({ issue }, index) => {
    // Keyed on the whole finding, not on its code alone. A checker reports one
    // code many times — eleven `W_UNUSED` at eleven lines are eleven findings,
    // and collapsing them to one undercounts exactly the list the reader opened
    // this section to read. Folding by shared cause is what the grouping below
    // is for; this pass only removes true duplicates.
    const id = findingKey(issue) || `${index}`;
    if (seenIssueIds.has(id)) return false;
    seenIssueIds.add(id);
    return true;
  });

  const groupsByKey = new Map<string, ValidationRootCauseGroup>();
  for (const { issue, severity } of uniqueIssues) {
    const classified = classifyValidationRootCause(issue);
    const category = normalizeText(issue.category || issue.kind || issue.source);
    const line = issueLine(issue);
    const message = normalizeText(issue.message || issue.detail || issue.reason || issue.title);

    const existing = groupsByKey.get(classified.key);
    if (existing) {
      existing.count += 1;
      if (line && !existing.lines.includes(line)) existing.lines.push(line);
      if (message && !existing.messages.includes(message) && existing.messages.length < 3) existing.messages.push(message);
    } else {
      groupsByKey.set(classified.key, {
        key: classified.key,
        title: classified.title,
        category,
        severity,
        count: 1,
        lines: line ? [line] : [],
        messages: message ? [message] : [],
        deterministicRepairId: classified.deterministicRepairId,
      });
    }
  }

  const groups = [...groupsByKey.values()].sort((a, b) => b.count - a.count || a.title.localeCompare(b.title));
  const errorRootCauseCount = groups.filter(group => normalizeSeverity(group.severity) === 'error').length;
  return {
    rootCauseCount: groups.length,
    errorRootCauseCount,
    warningRootCauseCount: Math.max(0, groups.length - errorRootCauseCount),
    rawIssueCount: uniqueIssues.length,
    reportedIssueCount: issues.length,
    groups,
  };
}

export function getValidationRootCauseSummary(report: unknown): ValidationRootCauseSummary {
  const record = asRecord(report);
  const existing = asRecord(record?.rootCauseSummary);
  if (existing?.groups) {
    return buildValidationRootCauseSummary({ ...record, rootCauseSummary: existing });
  }
  return buildValidationRootCauseSummary(report);
}

export function formatValidationRootCauseCount(report: unknown, review?: unknown, language?: DeliveryLabelLanguage): string {
  const summary = getValidationRootCauseSummary(report);
  if (summary.rootCauseCount <= 0) return deliveryValidationLabel('校验通过', review, language);
  if (summary.rawIssueCount > summary.rootCauseCount) {
    return `${summary.rootCauseCount} 类根因（共 ${summary.rawIssueCount} 处）`;
  }
  return `${summary.rootCauseCount} 类根因`;
}

export function getValidationRootCausePreview(report: unknown, limit = 3): ValidationRootCauseGroup[] {
  return getValidationRootCauseSummary(report).groups.slice(0, limit);
}
